<template>
  <div ref="root" class="aps-viewer-container" :class="{ 'is-fullscreen': isFullscreen }">
    <div ref="viewerContainer" class="aps-viewer-canvas" />

    <button
      type="button"
      class="aps-viewer-fs-btn"
      :title="$t('fullscreen')"
      @click="toggleFullscreen"
    >
      <b-icon pack="fas" :icon="isFullscreen ? 'compress' : 'expand'" size="is-small" />
    </button>

    <div v-if="loading" class="aps-viewer-overlay">
      <b-icon pack="fas" icon="spinner" custom-class="fa-spin" size="is-large" />
      <span class="ml-2">{{ loadingMessage }}</span>
    </div>

    <div v-if="error" class="aps-viewer-error">
      <b-icon pack="fas" icon="exclamation-triangle" size="is-small" />
      {{ error }}
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import store from '~/store'

const VIEWER_VERSION = '7.*'
const VIEWER_JS = `https://developer.api.autodesk.com/modelderivative/v2/viewers/${VIEWER_VERSION}/viewer3D.min.js`
const VIEWER_CSS = `https://developer.api.autodesk.com/modelderivative/v2/viewers/${VIEWER_VERSION}/style.min.css`

export default {
  name: 'aps-viewer',

  props: {
    // URN base64 del derivative (viene en relationships.derivatives.data.id de la versión).
    urn: { type: String, required: true },
    // ID del proyecto EPC — el backend valida URN contra la ACL de este proyecto
    // antes de emitir el token del viewer. Sin project_id no hay ACL check.
    projectId: { type: [Number, String], required: true },
    // EMEA / US / AUS. Determina el endpoint de streaming del viewer.
    region: { type: String, default: 'EMEA' },
    // Configuración adicional del viewer (extensiones, etc.).
    viewerConfig: { type: Object, default: () => ({}) },
  },

  data: () => ({
    viewer: null,
    loading: true,
    loadingMessage: '',
    error: null,
    isFullscreen: false,
  }),

  async mounted() {
    this.loadingMessage = this.$t('autodesk.viewer.loading_library')
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    try {
      await this.ensureLibraryLoaded()
      this.loadingMessage = this.$t('autodesk.viewer.initializing')
      await this.initViewer()
      this.loadingMessage = this.$t('autodesk.viewer.loading_model')
      await this.loadModel(this.urn)
      this.loading = false
      this.$emit('ready', this.viewer)
    } catch (err) {
      this.loading = false
      this.error = err.message || this.$t('autodesk.viewer.error_generic')
      this.$emit('error', err)
    }
  },

  beforeDestroy() {
    this._initReject = null
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    if (this.viewer) {
      try {
        this.viewer.tearDown()
        this.viewer.finish()
      } catch (e) {
        // silent
      }
      this.viewer = null
    }
  },

  watch: {
    urn(newUrn, oldUrn) {
      if (newUrn && newUrn !== oldUrn && this.viewer) {
        this.loading = true
        this.loadingMessage = this.$t('autodesk.viewer.loading_model')
        this.loadModel(newUrn).finally(() => { this.loading = false })
      }
    },
  },

  computed: {
    apiEndpoint() {
      // Autodesk usa endpoints de streaming distintos por region.
      // Ver: https://aps.autodesk.com/en/docs/viewer/v7/developers_guide/viewer_basics/env-values
      if (this.region === 'US') return 'streamingV2'
      if (this.region === 'AUS') return 'streamingV2_AUS'
      return 'streamingV2_EU'
    },
  },

  methods: {
    ensureLibraryLoaded() {
      return new Promise((resolve, reject) => {
        if (window.Autodesk && window.Autodesk.Viewing) {
          resolve()
          return
        }

        // CSS
        if (!document.querySelector(`link[data-aps-viewer]`)) {
          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = VIEWER_CSS
          link.setAttribute('data-aps-viewer', '1')
          document.head.appendChild(link)
        }

        // JS
        if (!document.querySelector(`script[data-aps-viewer]`)) {
          const script = document.createElement('script')
          script.src = VIEWER_JS
          script.setAttribute('data-aps-viewer', '1')
          script.onload = resolve
          script.onerror = () => reject(new Error('Failed to load Autodesk Viewer library'))
          document.head.appendChild(script)
        } else {
          resolve()
        }
      })
    },

    initViewer() {
      return new Promise((resolve, reject) => {
        // Initializer no llama al callback si getAccessToken falla: sin este
        // reject el visor se queda en "Inicializando visor…" para siempre.
        this._initReject = reject
        const options = {
          env: 'AutodeskProduction2',
          api: this.apiEndpoint,
          getAccessToken: (onSuccess, onError) => {
            this.fetchViewerToken(onSuccess, (err) => {
              const error = this.viewerTokenError(err)
              if (onError) onError(error)
              this.failViewerStart(error)
            })
          },
        }

        window.Autodesk.Viewing.Initializer(options, () => {
          if (!this._initReject) return
          try {
            this.viewer = new window.Autodesk.Viewing.GuiViewer3D(
              this.$refs.viewerContainer,
              this.viewerConfig
            )
            const code = this.viewer.start()
            if (code !== 0) {
              this.failViewerStart(new Error('Viewer failed to start (code ' + code + ')'))
              return
            }
            this._initReject = null
            resolve()
          } catch (e) {
            this.failViewerStart(e)
          }
        })
      })
    },

    failViewerStart(error) {
      if (this._isDestroyed) return
      this.loading = false
      this.error = (error && error.message) || this.$t('autodesk.viewer.error_generic')
      if (this._initReject) {
        const rejectInit = this._initReject
        this._initReject = null
        rejectInit(error)
      }
    },

    // 401 del viewer-token = sesión APS caducada o empresa sin conexión.
    // 502 = fallo transitorio refrescando el token (la conexión sigue viva).
    // El interceptor de axios rechaza con el response, no con un Error.
    viewerTokenError(err) {
      const status = err && err.status
      const apiMessage = (err && err.data && err.data.message) || (err && err.message) || ''
      if (status === 502) {
        return new Error(this.$t('autodesk.viewer.temporarily_unavailable'))
      }
      if (status === 401 || /session expired|please reconnect|not connected/i.test(apiMessage)) {
        return new Error(this.$t('autodesk.viewer.session_lost'))
      }
      if (err instanceof Error && err.message) return err
      return new Error(apiMessage || this.$t('autodesk.viewer.error_generic'))
    },

    async fetchViewerToken(onSuccess, onError) {
      try {
        // El backend valida que la URN esté en la ACL del proyecto EPC antes
        // de emitir el token. Si no autorizado → 403.
        const { data } = await axios.get(
          `/api/v2/autodesk/aps/projects/${this.projectId}/viewer-token`,
          {
            headers: { Authorization: 'Bearer ' + store.getters['auth/token'] },
            params: { urn: this.urn },
          }
        )
        if (data && data.success && data.data) {
          onSuccess(data.data.access_token, data.data.expires_in)
        } else {
          const msg = (data && data.message) || 'Viewer token error'
          if (onError) onError(new Error(msg))
        }
      } catch (err) {
        if (onError) onError(err)
      }
    },

    loadModel(urn) {
      return new Promise((resolve, reject) => {
        const documentId = urn.startsWith('urn:') ? urn : 'urn:' + urn

        window.Autodesk.Viewing.Document.load(
          documentId,
          (doc) => {
            const defaultGeom = doc.getRoot().getDefaultGeometry()
            if (!defaultGeom) {
              reject(new Error('Model has no viewable geometry'))
              return
            }
            this.viewer.loadDocumentNode(doc, defaultGeom)
              .then(() => {
                this.$emit('model-loaded', { doc, urn })
                resolve()
              })
              .catch(reject)
          },
          (errorCode) => {
            reject(new Error('Failed to load document (code ' + errorCode + ')'))
          }
        )
      })
    },

    toggleFullscreen() {
      const el = this.$refs.root
      if (!el) return
      if (document.fullscreenElement) {
        document.exitFullscreen()
      } else if (el.requestFullscreen) {
        el.requestFullscreen()
      }
    },

    onFullscreenChange() {
      this.isFullscreen = document.fullscreenElement === this.$refs.root
      // El canvas WebGL de Autodesk se ata al tamaño inicial — forzamos resize
      // al entrar y salir de fullscreen para que redibuje a la resolución nueva.
      if (this.viewer && typeof this.viewer.resize === 'function') {
        this.$nextTick(() => this.viewer.resize())
      }
    },
  },
}
</script>

<style scoped>
.aps-viewer-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 500px;
  background: #1a1a1a;
}

/* En fullscreen (API HTML5) el contenedor pasa a ser hijo directo del <html>
   y necesita rellenar el 100% del viewport él mismo. */
.aps-viewer-container.is-fullscreen {
  width: 100vw;
  height: 100vh;
  min-height: 0;
}

.aps-viewer-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Botón fullscreen — esquina superior derecha, por encima del toolbar Autodesk. */
.aps-viewer-fs-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 30;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.aps-viewer-fs-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

.aps-viewer-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  background: rgba(0, 0, 0, 0.6);
  padding: 1rem 1.5rem;
  border-radius: 4px;
}

.aps-viewer-error {
  position: absolute;
  top: 1rem;
  left: 1rem;
  right: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(220, 53, 69, 0.95);
  color: white;
  border-radius: 4px;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
